import { Dispatch, SetStateAction, useCallback } from 'react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { validateUUIDV4 } from '@frontend/string';
import {
  cleanPhoneNumberSearchQuery,
  getDefaultSearchType,
  hasLetters,
  isContentSearchType,
} from '../../../utils/call-intelligence';

type UseCallRecordsSearchHandlersProps = {
  isFetching: boolean;
  isTranscriptSearchEnabled: boolean;
  searchQuery: CallIntelTypes.SearchState;
  setSearchQuery: Dispatch<SetStateAction<CallIntelTypes.SearchState>>;

  minSearchLength: number;
  defaultSearchType: CallIntelTypes.SearchTypeEnum;
  defaultTableSortType: CallIntelTypes.SortType;

  onPaginationAfterApplyingFilters: (sortType?: CallIntelTypes.SortType) => void;

  resetTranscriptSearchHistory: () => void;
  startTranscriptSearchHistory: (query: string, searchType?: CallIntelTypes.SearchTypeEnum) => void;
};

export const useCallRecordsSearchHandlers = ({
  isFetching,
  isTranscriptSearchEnabled,
  searchQuery,
  setSearchQuery,
  minSearchLength,
  defaultSearchType,
  defaultTableSortType,
  onPaginationAfterApplyingFilters,
  resetTranscriptSearchHistory,
  startTranscriptSearchHistory,
}: UseCallRecordsSearchHandlersProps) => {
  const hasTranscriptSearchQuery =
    isTranscriptSearchEnabled && !!searchQuery.query && isContentSearchType(searchQuery.searchType) && !isFetching;

  /**
   * Handles search input changes for both normal search and transcript search.
   * Search behavior:
   * - Empty search:
   *   - Clears both the UI value and committed API query.
   *   - Resets pagination and sort back to default DESC.
   *   - This handles both clear button and manual backspace-to-empty flows.
   *
   * - UUID search:
   *   - Uses the trimmed UUID as the committed query.
   *   - Always uses default DESC sorting.
   *   - API payload generation treats it as a call ID filter instead of a normal search query.
   *
   * - Phone number search:
   *   - Only cleans phone formatting when the final search type is SEARCH_BY_NUMBER
   *     and the input is number-like.
   *   - Example: "(123) 456-7890" becomes "1234567890" for the API.
   *   - The UI still keeps the original typed value.
   *   - If SEARCH_BY_NUMBER is selected but the user types alphabetic text,
   *     keep the original text so the API can return an empty result state.
   *   - Do not convert invalid number searches into an empty query, because
   *     an empty query means reset to the full table.
   *
   * - Transcript/content search:
   *   - Uses SORT_RELEVANCE only when transcript search is enabled,
   *     the selected search type is SEARCH_BY_CONTENT,
   *     and there is a valid committed search query.
   *   - Empty or below-min-length queries always fall back to default DESC sorting.
   *
   * - Normal/default search:
   *   - Uses SEARCH_BY_NAME or SEARCH_BY_NUMBER based on getDefaultSearchType.
   *   - Always uses default DESC sorting.
   */
  const handleSearchOnChange = useCallback(
    (value: string, debounced?: boolean) => {
      const trimmedValue = value.trim();
      const isUUIDSearch = validateUUIDV4(trimmedValue);

      const nextSearchType = isTranscriptSearchEnabled
        ? searchQuery.searchType ?? defaultSearchType
        : getDefaultSearchType(value);

      const isNumberSearch = nextSearchType === CallIntelTypes.SearchTypeEnum.SEARCH_BY_NUMBER;
      const cleanedPhoneNumberQuery = cleanPhoneNumberSearchQuery(value);

      const shouldCleanPhoneNumberQuery =
        isNumberSearch && !isUUIDSearch && !!cleanedPhoneNumberQuery && !hasLetters(trimmedValue);

      const queryValue = shouldCleanPhoneNumberQuery ? cleanedPhoneNumberQuery : trimmedValue;

      const hasSearchQuery = queryValue.length >= minSearchLength;

      const nextSortType =
        hasSearchQuery && !isUUIDSearch && isTranscriptSearchEnabled && isContentSearchType(nextSearchType)
          ? CallIntelTypes.SortType.SORT_RELEVANCE
          : defaultTableSortType;

      if (value === '') {
        resetTranscriptSearchHistory();

        setSearchQuery({
          query: '',
          value: '',
          searchType: nextSearchType,
        });

        onPaginationAfterApplyingFilters(defaultTableSortType);
        return;
      }

      if (!debounced) {
        setSearchQuery((prev) => ({
          ...prev,
          value,
          searchType: nextSearchType,
        }));

        return;
      }

      if (hasSearchQuery) {
        startTranscriptSearchHistory(queryValue, nextSearchType);

        setSearchQuery({
          value,
          query: queryValue,
          searchType: nextSearchType,
        });

        onPaginationAfterApplyingFilters(nextSortType);
        return;
      }

      resetTranscriptSearchHistory();

      setSearchQuery({
        query: '',
        value,
        searchType: nextSearchType,
      });

      onPaginationAfterApplyingFilters(defaultTableSortType);
    },
    [
      defaultSearchType,
      defaultTableSortType,
      isTranscriptSearchEnabled,
      minSearchLength,
      onPaginationAfterApplyingFilters,
      resetTranscriptSearchHistory,
      searchQuery.searchType,
      setSearchQuery,
      startTranscriptSearchHistory,
    ]
  );

  const handleSearchTypeChange = useCallback(
    (searchType: CallIntelTypes.SearchTypeEnum) => {
      setSearchQuery((prev) => ({
        ...prev,
        searchType,
      }));

      const hasSearchText = !!searchQuery.query.trim();

      const nextSortType =
        hasSearchText && isContentSearchType(searchType)
          ? CallIntelTypes.SortType.SORT_RELEVANCE
          : defaultTableSortType;

      onPaginationAfterApplyingFilters(nextSortType);
    },
    [defaultTableSortType, onPaginationAfterApplyingFilters, searchQuery.query, setSearchQuery]
  );

  return {
    handleSearchOnChange,
    handleSearchTypeChange,
    hasTranscriptSearchQuery,
  };
};
